import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useConfigurationStore } from 'store/configurationsStore';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { TableFooter } from 'components/Dashboards/Project/Components/VersionCompare/TableFotter/TableFooter';
import classes from 'components/Dashboards/Project/Components/VersionCompare/style/Table.module.scss';
import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { smallPageSize } from 'components/Dashboards/Project/Components/VersionCompare/utils/table.utils';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Tabs } from 'components/DesignSystem/Tabs';
import { StatusFilter } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/filters/statusFilter';
import { TableLocalFilters } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/tableLocalFilters';
import { useCriticalPathRemovedLogic } from 'components/widgets/CriticalPathRemoved/CriticalPathRemoved.logic';
import { useCriticalPathRemovedProvider } from 'components/widgets/CriticalPathRemoved/CriticalPathRemoved.provider';
import { columnsV8, initialState } from 'components/widgets/CriticalPathRemoved/CriticalPathRemoved.utils';
import classesGeneralWidget from 'components/widgets/Utils/Styles/widget.module.scss';

export const CriticalPathRemoved = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    isSinglePage = false,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propsTitle,
    setIsPayloadReady,
}: ITableWidgetWrapperProps) => {
    const { componentKey, routeId: id, elementId, endpoints, name: configTitle } = useConfigurationStore().criticalPathRemoved;

    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        initialPagination: { page: 1, size: 5 },
        widgetViewMode,
    });

    // Provider
    const { ui, api } = useCriticalPathRemovedProvider({
        componentKey,
        widgetId,
        globalFilterData,
        localFilters,
        table,
        compareVersionId,
        latestVersionId,
        title: propsTitle ?? configTitle,
        projectId,
        contractId,
        widgetViewMode,
        endpoints,
    });

    // Widget
    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        title: propsTitle ?? configTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getCriticalPathRemovedTableData.payload,
        widgetEndpoint: api.getCriticalPathRemovedTableData.endpoint,
    });

    // Logic
    const { logic } = useCriticalPathRemovedLogic({
        ui,
        api,
        title: propsTitle ?? configTitle,
        subTitle,
        table,
        isSinglePage,
        setIsPayloadReady,
        widgetViewMode,
    });

    return (
        <WidgetWithTitle
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                id={id}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,

                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    !WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode) && (
                        <TableLocalFilters key={'filters'}>
                            <StatusFilter control={ui.form.control} />
                        </TableLocalFilters>
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={propsTitle ?? configTitle}
                        projectId={projectId}
                        contractId={contractId}
                        elementId={elementId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        callBack={table.setWidgetColumns}
                        shouldHide={true}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        callBack={table.setWidgetColumns}
                        apiPayload={api.getCriticalPathRemovedTableData.payload}
                        widgetEndpoint={api.getCriticalPathRemovedTableData.endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown
                            key={'export'}
                            title={propsTitle ?? configTitle}
                            subTitle={subTitle}
                            handleAPIExcelExport={logic.handleAPIExcelExport}
                        />
                    ),
                    !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <ColumnsManager
                            onOpenModal={() => table.setModalColumnsOpen(true)}
                            onHideModal={() => table.setModalColumnsOpen(false)}
                            showModal={table.modalColumnsOpen}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            tableInstance={table.tableInstance}
                            initialState={initialState}
                            columns={columnsV8}
                            widgetViewMode={widgetViewMode}
                        />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getCriticalPathRemovedTableData.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={propsTitle ?? configTitle}
                            shareLink={route}
                        />
                    ),
                ]}
            >
                {logic.tableData && (
                    <TableContainer className={classes.tableWrapperContainer}>
                        {logic.tabsArrData.length > 0 && (
                            <Tabs
                                tabs={logic.tabsArrData}
                                value={ui.state.activeTab}
                                onChange={(value) => ui.setters.setActiveTab(value as TKeys)}
                            />
                        )}

                        <TableV8
                            data={logic.tableData}
                            columns={columnsV8}
                            isLoading={
                                api.getCriticalPathRemovedTableData.isLoading ||
                                api.getCriticalPathRemovedTableData.isFetching
                            }
                            noData={<NoData>No Changes To Display</NoData>}
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            manualPagination={false}
                            manualSorting={false}
                            pagination={table.pagination}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                            tableClassName={
                                WidgetViewModeUtils.isReportView(widgetViewMode)
                                    ? classesGeneralWidget.reportTableScrollable
                                    : classesGeneralWidget.pageTableScrollable
                            }
                            wrapperClassName={
                                WidgetViewModeUtils.isInAnyReportMode(widgetViewMode)
                                    ? classesGeneralWidget.reportTableWrapper
                                    : ''
                            }
                        />

                        {logic.tableData.length > smallPageSize &&
                            !isSinglePage &&
                            !WidgetViewModeUtils.isInAnyReportMode(widgetViewMode) && (
                                <TableFooter
                                    setPageSize={(pageSize) => {
                                        table.setPagination({ ...table.pagination, size: pageSize });
                                    }}
                                    pageSize={table.pagination.size}
                                    link={!widgetId ? executionConfig.criticalPathRemoved.link : undefined}
                                />
                            )}
                    </TableContainer>
                )}

                {api.exportCsvReport.instance.isLoading && <OverlayWithSpinner />}
            </WidgetWithTitle>
    );
};
